import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, LogOut, User, Bell } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '@/LanguageContext';
import { api } from '@/utils/api';

const HCS_LOGO_URL = 'https://customer-assets.emergentagent.com/job_203c254d-590f-4582-b077-890a39b58283/artifacts/vvnnctky_HCS%20Logo.jpg';

const Navbar = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [showMenu, setShowMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Check if user is an admin who can see risk notifications
  const isAdmin = user?.role && ['super_admin', 'state_admin', 'district_admin', 'administrator'].includes(user.role);

  // Fetch unread notification count for admins
  useEffect(() => {
    if (isAdmin) {
      const fetchUnreadCount = async () => {
        try {
          const res = await api.getUnreadNotificationCount();
          setUnreadCount(res.data.unread_count || 0);
        } catch (error) {
          console.error('Error fetching unread count:', error);
        }
      };
      
      fetchUnreadCount();
      // Poll every 60 seconds
      const interval = setInterval(fetchUnreadCount, 60000);
      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  const getRoleDisplay = (role) => {
    if (role === 'caregiver') return t('auth.caregiver');
    if (role === 'frontline_worker') return t('auth.frontlineWorker');
    if (role === 'administrator') return t('auth.administrator');
    if (role === 'super_admin') return 'Super Admin';
    if (role === 'state_admin') return 'State Admin';
    if (role === 'district_admin') return 'District Admin';
    return role;
  };

  const handleNotificationClick = () => {
    if (isAdmin) {
      navigate('/risk-notifications');
    } else {
      navigate('/notifications');
    }
  };

  return (
    <nav className="bg-white border-b-4 border-[#0F4C81] shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div 
            className="flex items-center cursor-pointer group" 
            onClick={() => navigate('/dashboard')}
            data-testid="navbar-logo"
          >
            <img 
              src={HCS_LOGO_URL} 
              alt="HCS Logo" 
              className="h-12 w-auto object-contain"
            />
            <span className="ml-3 text-xl font-bold text-[#0F4C81] font-['Nunito']">ECD-AI Connect</span>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-[#4B5563]">
              <User size={18} />
              <span className="text-sm font-medium">{user?.name}</span>
              <span className="text-xs bg-[#F59E0B] text-black px-2 py-1 rounded-full font-bold">
                {getRoleDisplay(user?.role)}
              </span>
            </div>
            
            {/* Notifications Button */}
            <button
              onClick={handleNotificationClick}
              data-testid="notifications-button"
              className="p-2 rounded-full hover:bg-gray-100 transition-colors relative"
              title={isAdmin ? 'Risk Notifications' : t('notifications.title')}
            >
              <Bell size={20} className="text-[#0F4C81]" />
              {isAdmin && unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#EF4444] text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1" data-testid="unread-badge">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            
            <LanguageSelector />
            
            <button
              onClick={onLogout}
              data-testid="logout-button"
              className="flex items-center space-x-2 px-4 py-2 bg-[#EF4444] text-white rounded-full font-semibold hover:bg-[#DC2626] transition-all active:scale-95"
            >
              <LogOut size={18} />
              <span>{t('common.logout')}</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setShowMenu(!showMenu)}
            data-testid="mobile-menu-button"
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Menu size={24} className="text-[#0F4C81]" />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {showMenu && (
        <div className="md:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-3">
            <div className="flex items-center space-x-2 text-[#4B5563] pb-3 border-b">
              <User size={18} />
              <span className="text-sm font-medium">{user?.name}</span>
              <span className="text-xs bg-[#F59E0B] text-black px-2 py-1 rounded-full font-bold">
                {getRoleDisplay(user?.role)}
              </span>
            </div>
            
            {/* Notifications Button - Mobile */}
            <button
              onClick={() => { handleNotificationClick(); setShowMenu(false); }}
              data-testid="mobile-notifications-button"
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-[#0F4C81] text-white rounded-xl font-bold hover:bg-[#0d3d6b] transition-all relative"
            >
              <Bell size={18} />
              <span>{isAdmin ? 'Risk Notifications' : t('notifications.title')}</span>
              {isAdmin && unreadCount > 0 && (
                <span className="bg-[#EF4444] text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            
            <LanguageSelector />
            
            <button
              onClick={onLogout}
              data-testid="mobile-logout-button"
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-[#EF4444] text-white rounded-xl font-bold hover:bg-[#DC2626] transition-all"
            >
              <LogOut size={18} />
              <span>{t('common.logout')}</span>
            </button>
          </div>
        </div>
      )}
      
    </nav>
  );
};

export default Navbar;